import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiHeart,
  FiMessageCircle,
  FiX,
  FiSend,
  FiZap,
  FiUsers,
  FiFileText,
  FiEye,
  FiRefreshCw,
  FiShare2,
  FiBookmark,
  FiBarChart2,
  FiSliders,
  FiSearch,
  FiChevronDown,
  FiCalendar,
  FiSmile,
  FiPlus,
  FiExternalLink,
  FiGrid,
  FiPlayCircle,
} from "react-icons/fi";
import {
  connectInstagramAccount,
  disconnectInstagramAccount,
  fetchInstagramAccountData,
  fetchInstagramMediaComments,
  fetchProfileInfo,
  replyToComment,
} from "./instagramApi";
import DashboardLayout from "../../UI/Layout/DashboardLayout";
import styles from "./Instagram.module.css";
const instagramPng = "/img/instagram.png";

const INSTAGRAM_APP_ID = process.env.REACT_APP_INSTAGRAM_APP_ID;

const INSTAGRAM_PERMISSION = process.env.REACT_APP_INSTAGRAM_PERMISSION;

const INSTAGRAM_AUTH_ENDPOINT = process.env.REACT_APP_INSTAGRAM_AUTH_ENDPOINT;

const INSTAGRAM_REDIRECT_URI = process.env.REACT_APP_INSTAGRAM_REDIRECT_URI;

// Adjust this if your router registers InstagramAutomationSetup under a
// different path.
const INSTAGRAM_AUTOMATION_ROUTE = "/dashboard/instagram-automation";

// Per-post ORM report page — pre-filtered to the post the user clicked from.
// const INSTAGRAM_REPORT_ROUTE = "/dashboard/instagram-report";

const DATE_RANGE_OPTIONS = [
  { key: "all", label: "All Time" },
  { key: "today", label: "Today" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "custom", label: "Custom Date Range" },
];

const SENTIMENT_OPTIONS = [
  { key: "all", label: "All Sentiments" },
  { key: "positive", label: "Positive" },
  { key: "neutral", label: "Neutral" },
  { key: "negative", label: "Negative" },
];

const SORT_OPTIONS = [
  { key: "newest", label: "Newest First" },
  { key: "oldest", label: "Oldest First" },
];

const SENTIMENT_TAG_CLASS = {
  positive: styles.sentimentTagPositive,
  neutral: styles.sentimentTagNeutral,
  negative: styles.sentimentTagNegative,
};

const SENTIMENT_TAG_LABEL = {
  positive: "Positive",
  neutral: "Neutral",
  negative: "Negative",
};

const formatSmartTime = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 5) return "Just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
};

const formatFullDateTime = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const formatShortPostId = (id) => {
  if (id === undefined || id === null || id === "") return "";
  const str = String(id);
  if (str.length <= 8) return `#${str}`;
  return `#${str.slice(-6)}`;
};

const getCommentSentiment = (comment) => {
  const raw =
    comment?.sentiment ??
    comment?.sentiment_label ??
    comment?.sentimentLabel ??
    null;

  if (!raw) return null;

  const normalized = String(raw).toLowerCase();
  if (normalized.includes("pos")) return "positive";
  if (normalized.includes("neg")) return "negative";
  if (normalized.includes("neu")) return "neutral";
  return null;
};

const getCommentTopic = (comment) => {
  const explicit =
    comment?.matched_keyword || comment?.topic || comment?.keyword || null;
  if (explicit) return `#${String(explicit).replace(/^#/, "")}`;

  const text = comment?.text || comment?.message || "";
  const hashtagMatch = text.match(/#[\w-]+/);
  return hashtagMatch ? hashtagMatch[0] : null;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isWithinDateRange = (timestamp, range, customFrom, customTo) => {
  if (range === "all" || !timestamp) return true;

  const commentDate = new Date(timestamp);
  if (Number.isNaN(commentDate.getTime())) return true;

  const now = new Date();

  if (range === "today") {
    return startOfDay(commentDate).getTime() === startOfDay(now).getTime();
  }

  if (range === "7d") {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 7);
    return commentDate >= cutoff;
  }

  if (range === "30d") {
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - 30);
    return commentDate >= cutoff;
  }

  if (range === "custom") {
    if (customFrom && commentDate < startOfDay(customFrom)) return false;
    if (customTo) {
      const end = startOfDay(customTo);
      end.setHours(23, 59, 59, 999);
      if (commentDate > end) return false;
    }
    return true;
  }

  return true;
};

const Instagram = () => {
  const [instagramData, setInstagramData] = useState(null);

  const [isFetching, setIsFetching] = useState(false);

  // Posts-toolbar: media-type tabs.
  const [mediaTypeFilter, setMediaTypeFilter] = useState("all");

  // Posts-toolbar: date range filter 
  const [postDateRangeFilter, setPostDateRangeFilter] = useState("all");
  const [postCustomDateFrom, setPostCustomDateFrom] = useState("");
  const [postCustomDateTo, setPostCustomDateTo] = useState("");
  const [postDateFilterOpen, setPostDateFilterOpen] = useState(false);
  const postDateFilterRef = useRef(null);

  useEffect(() => {
    if (!postDateFilterOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        postDateFilterRef.current &&
        !postDateFilterRef.current.contains(event.target)
      ) {
        setPostDateFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [postDateFilterOpen]);

  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const [isConnected, setIsConnected] = useState(
    localStorage.getItem("instagram_connected") === "true",
  );

  const [instagramProfile, setInstagramProfile] = useState(() => {
    const savedProfile = localStorage.getItem("instagramProfile");

    if (!savedProfile) {
      return null;
    }

    try {
      return JSON.parse(savedProfile);
    } catch (error) {
      return null;
    }
  });

  // Multi-account support — the list of every Instagram account connected
  // to this platform user, and which one (or "all") is currently active.
  const [connectedAccounts, setConnectedAccounts] = useState(() => {
    const saved = localStorage.getItem("instagramAccounts");

    if (!saved) return [];

    try {
      const parsed = JSON.parse(saved);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  });

  const [activeAccountId, setActiveAccountId] = useState(
    () => localStorage.getItem("instagramActiveAccountId") || "all",
  );

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [accountsLoaded, setAccountsLoaded] = useState(false);

  const [isAccountSwitching, setIsAccountSwitching] = useState(false);
  const isFirstAccountRender = useRef(true);

  const saveConnectedAccounts = (accounts) => {
    setConnectedAccounts(accounts);
    localStorage.setItem("instagramAccounts", JSON.stringify(accounts));
    window.dispatchEvent(
      new CustomEvent("instagram-accounts-changed", {
        detail: { accounts },
      }),
    );
  };

  const handleSelectAccount = (accountId) => {
    setActiveAccountId(accountId);
    localStorage.setItem("instagramActiveAccountId", accountId);
  };


  const activeAccount =
    activeAccountId === "all"
      ? null
      : connectedAccounts.find((account) => account.id === activeAccountId);
 
  // Max number of extra attempts if the backend reports zero accounts /
  // errors out. Right after the OAuth "?connected=true" redirect, the
  // backend can take a moment to finish provisioning the account, so an
  // immediate empty/failed response doesn't necessarily mean "not
  // connected" — it can just mean "not ready yet".
  const MAX_ACCOUNT_LOAD_RETRIES = 4;
  const ACCOUNT_LOAD_RETRY_DELAY_MS = 1200;

  const loadConnectedAccounts = async (retryCount = 0) => {
    if (!userId) return;

    let shouldRetry = false;

    try {
      setIsLoadingAccounts(true);

      const result = await fetchProfileInfo({ userId });
      const accounts = Array.isArray(result?.data) ? result.data : [];

      if (accounts.length === 0 && retryCount < MAX_ACCOUNT_LOAD_RETRIES) {
        // Don't treat this as "disconnected" yet — the account may still
        // be propagating on the backend right after connecting. Retry
        // with a short backoff instead of surfacing a false error.
        shouldRetry = true;
      } else {
        saveConnectedAccounts(accounts);

        if (accounts.length > 0) {
          markInstagramConnected();

          const raw = Array.isArray(result?.rawProfiles)
            ? result.rawProfiles[0]
            : null;
          if (raw?.username) {
            saveInstagramProfile({
              ...raw,
              insta_user_id:
                raw.insta_user_id || accounts[0]?.instaUserId || accounts[0]?.id,
            });
          }

          setActiveAccountId((current) => {
            const stillExists =
              current === "all" ||
              accounts.some(
                (account) =>
                  String(account.id) === String(current) ||
                  String(account.instaUserId) === String(current),
              );
            const next = stillExists ? current : "all";
            localStorage.setItem("instagramActiveAccountId", next);
            return next;
          });
        } else {
          const legacyStatus = localStorage.getItem("instagram_connected");
          if (legacyStatus !== "true") {
            setIsConnected(false);
          }
        }
      }
    } catch (error) {
      if (retryCount < MAX_ACCOUNT_LOAD_RETRIES) {
        shouldRetry = true;
      }
    } finally {
      setIsLoadingAccounts(false);
      // Only flip accountsLoaded once we're done retrying — this is what
      // unlocks the getInstagramData fetch below, so it must not fire
      // while we still might be looking at a stale/incomplete account list.
      if (!shouldRetry) {
        setAccountsLoaded(true);
      }
    }

    if (shouldRetry) {
      setTimeout(
        () => loadConnectedAccounts(retryCount + 1),
        ACCOUNT_LOAD_RETRY_DELAY_MS * (retryCount + 1),
      );
    }
  };

  const [successMessage, setSuccessMessage] = useState("");

  const [errorMessage, setErrorMessage] = useState("");

  
  const stickyHeaderRef = useRef(null);
  const [stickyHeaderOffset, setStickyHeaderOffset] = useState(64);
  const [fixedBarHeight, setFixedBarHeight] = useState(64);
  const [fixedBarLeft, setFixedBarLeft] = useState(0);
  const [fixedBarWidth, setFixedBarWidth] = useState("100%");

  useEffect(() => {
    const MOBILE_BREAKPOINT = 768;

    const measure = () => {
      const globalNavbar = document.querySelector(".navbar.dashboard-header");
      if (globalNavbar) {
        const rect = globalNavbar.getBoundingClientRect();
        const top = Math.max(0, Math.ceil(rect.bottom));
        if (top > 0) setStickyHeaderOffset(top);
      }

      const isMobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const mainEl = document.querySelector(".main");

      if (isMobile) {
        setFixedBarLeft(0);
        setFixedBarWidth("100%");
      } else if (mainEl) {
        const mainRect = mainEl.getBoundingClientRect();
        setFixedBarLeft(Math.max(0, Math.round(mainRect.left)));
        setFixedBarWidth(`${Math.round(mainRect.width)}px`);
      }

      if (stickyHeaderRef.current) {
        const h = Math.ceil(
          stickyHeaderRef.current.getBoundingClientRect().height,
        );
        if (h > 0) setFixedBarHeight(h);
      }
    };

    measure();
    const raf = requestAnimationFrame(measure);
    const timers = [120, 300, 600, 1000].map((delay) =>
      setTimeout(measure, delay),
    );

    const globalNavbar = document.querySelector(".navbar.dashboard-header");
    const mainEl = document.querySelector(".main");
    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro && globalNavbar) ro.observe(globalNavbar);
    if (ro && mainEl) ro.observe(mainEl);
    if (ro && stickyHeaderRef.current) ro.observe(stickyHeaderRef.current);

    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      cancelAnimationFrame(raf);
      timers.forEach((t) => clearTimeout(t));
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [isConnected, connectedAccounts.length]);

  // Both success and error banners are transient — clear them a few
  // seconds after they appear instead of leaving them on screen.
  useEffect(() => {
    if (!successMessage) return;

    const timer = setTimeout(() => setSuccessMessage(""), 3500);

    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!errorMessage) return;

    const timer = setTimeout(() => setErrorMessage(""), 4500);

    return () => clearTimeout(timer);
  }, [errorMessage]);

  const loginData = JSON.parse(localStorage.getItem("login"));

  const [selectedPostId, setSelectedPostId] = useState(null);

  const [commentsByPostId, setCommentsByPostId] = useState({});

  const [commentsLoadingId, setCommentsLoadingId] = useState(null);

  const [replyInputs, setReplyInputs] = useState({});

  const [replyBoxOpen, setReplyBoxOpen] = useState({});

  const [sendingReplyId, setSendingReplyId] = useState(null);

  // Comments Stream filters (client-side — narrows the already-loaded
  // comments list for the selected post).
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [dateRangeMenuOpen, setDateRangeMenuOpen] = useState(false);
  const [dateRangeFilter, setDateRangeFilter] = useState("all");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [commentSearch, setCommentSearch] = useState("");
  const [sentimentFilter, setSentimentFilter] = useState("all");
  const [commentSortOrder, setCommentSortOrder] = useState("newest");

  const resetCommentFilters = () => {
    setFiltersExpanded(false);
    setDateRangeFilter("all");
    setCustomDateFrom("");
    setCustomDateTo("");
    setCommentSearch("");
    setSentimentFilter("all");
    setCommentSortOrder("newest");
  };

  const userId = loginData?.user?.userId;
  const instagramUsername =
    instagramProfile?.username || localStorage.getItem("instagramUsername");

  const markInstagramConnected = () => {
    setIsConnected(true);
    localStorage.setItem("instagram_connected", "true");
  };

  const saveInstagramProfile = (profile) => {
    if (!profile) {
      return;
    }

    const instagramUsername = profile.username;
    setInstagramProfile(profile);
    localStorage.setItem("instagramProfile", JSON.stringify(profile));

    if (instagramUsername) {
      localStorage.setItem("instagramUsername", instagramUsername);
    }
  };

  const loadInstagramProfile = async (options = {}) => {
  if (!userId) return;

  try {
    const result = await fetchProfileInfo({
      userId,
      instaUserId: activeAccountId !== "all" ? activeAccountId : undefined,
    });
    const normalized = Array.isArray(result?.data) ? result.data : [];
    const rawProfiles = Array.isArray(result?.rawProfiles) ? result.rawProfiles : [];

    const matchesActive = (p) =>
      activeAccountId === "all" ||
      String(p?.insta_user_id ?? p?.instaUserId ?? p?.id) === String(activeAccountId);

    const matchedRaw = rawProfiles.find(matchesActive);
    const matchedNormalized = normalized.find(matchesActive);

    const profile =
      matchedRaw ||
      rawProfiles[0] ||
      (matchedNormalized || normalized[0]
        ? {
            username: (matchedNormalized || normalized[0]).username,
            insta_user_id:
              (matchedNormalized || normalized[0]).instaUserId ||
              (matchedNormalized || normalized[0]).id,
            followers_count: (matchedNormalized || normalized[0]).followersCount,
            profile_picture_url: (matchedNormalized || normalized[0]).avatarUrl,
          }
        : null);

    if (profile?.username || (result?.success && profile)) {
      markInstagramConnected();
      saveInstagramProfile(profile);
    } else if (
      !options.keepExistingStatus &&
      localStorage.getItem("instagram_connected") !== "true"
    ) {
      setIsConnected(false);
    }
  } catch (error) {
    if (
      !options.keepExistingStatus &&
      localStorage.getItem("instagram_connected") !== "true"
    ) {
      setIsConnected(false);
    }
  }
};

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const connected = params.get("connected");
    const storedCode = sessionStorage.getItem("instagram_auth_code");
    const storedState = sessionStorage.getItem("instagram_auth_state");
    const storedError = sessionStorage.getItem("instagram_auth_error");
    const code = params.get("code") || storedCode;
    const stateUserId = params.get("state") || storedState;
    const callbackError = params.get("error") || storedError;

    if (connected === "true") {
      markInstagramConnected();
      setSuccessMessage("Instagram account connected successfully.");

      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (callbackError) {
      setErrorMessage(decodeURIComponent(callbackError));
      sessionStorage.removeItem("instagram_auth_error");

      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (code) {
      const finalUserId = userId || stateUserId;

      sessionStorage.removeItem("instagram_auth_code");
      sessionStorage.removeItem("instagram_auth_state");

      if (!finalUserId) {
        return;
      }

      console.log("Instagram authorization code:", code);

      connectInstagramAccount({
        code,
        userId: finalUserId,
      })
        .then(() => {
          markInstagramConnected();
          setSuccessMessage("Instagram account connected successfully.");
          loadConnectedAccounts();
          loadInstagramProfile({ keepExistingStatus: true });

          window.history.replaceState(
            {},
            document.title,
            "/dashboard/instagram?connected=true",
          );
        })
        .catch((error) => {
          setErrorMessage(error.message || "Unable to connect Instagram.");

          window.history.replaceState(
            {},
            document.title,
            "/dashboard/instagram",
          );
        });
    }

    const savedStatus = localStorage.getItem("instagram_connected");

    if (savedStatus === "true") {
      setIsConnected(true);
    }

    loadConnectedAccounts();
    loadInstagramProfile({ keepExistingStatus: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (connectedAccounts.length === 0) return;

    const stillExists =
      activeAccountId !== "all" &&
      connectedAccounts.some(
        (account) =>
          String(account.id) === String(activeAccountId) ||
          String(account.instaUserId) === String(activeAccountId),
      );

    if (!stillExists) {
      handleSelectAccount(connectedAccounts[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId, connectedAccounts]);

  const handleConnectInstagram = () => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!userId) {
      setErrorMessage("Unable to find the logged-in user ID.");
      return;
    }

    if (!INSTAGRAM_APP_ID) {
      setErrorMessage("Instagram app ID is missing.");
      return;
    }

    try {
      const authUrl = `${INSTAGRAM_AUTH_ENDPOINT}?force_reauth=true&client_id=${encodeURIComponent(
        INSTAGRAM_APP_ID,
      )}&redirect_uri=${encodeURIComponent(
        INSTAGRAM_REDIRECT_URI,
      )}&response_type=code&scope=${encodeURIComponent(
        INSTAGRAM_PERMISSION,
      )}&state=${encodeURIComponent(userId)}`;

      console.log("Instagram OAuth URL:", authUrl);

      window.location.assign(authUrl);
    } catch (error) {
      setErrorMessage("Instagram authorization URL is invalid.");
    }
  };

  const handleFetchInstagramData = async ({ silent = false } = {}) => {
    setErrorMessage("");
    if (!silent) setSuccessMessage("");

    if (!userId) {
      if (!silent) setErrorMessage("Unable to find the logged-in user ID.");
      return;
    }
    if (!isConnected) {
      if (!silent) setErrorMessage("Instagram account is not connected.");
      return;
    }

    try {
      setIsFetching(true);

      const result = await fetchInstagramAccountData({
        userId,
        // "all" or a specific Instagram insta_user_id
        instaUserId: activeAccountId,
      });

      setInstagramData(result?.data || result);
      markInstagramConnected();

      if (!silent) setSuccessMessage("Instagram data fetched successfully.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to fetch Instagram data.");
    } finally {
      setIsFetching(false);
    }
  };


  useEffect(() => {
    setSelectedPostId(null);
    setCommentsByPostId({});

   
    if (!isFirstAccountRender.current && isConnected) {
      setInstagramData(null);
      setIsAccountSwitching(true);
    }
    isFirstAccountRender.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAccountId, isConnected]);

    useEffect(() => {
    if (
      isConnected &&
      userId &&
      accountsLoaded &&
      connectedAccounts.length > 0
    ) {
      Promise.allSettled([
        handleFetchInstagramData({ silent: true }),
        loadInstagramProfile({ keepExistingStatus: true }),
      ]).finally(() => setIsAccountSwitching(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, userId, activeAccountId, accountsLoaded, connectedAccounts]);

  const getInstagramItems = () => {
    if (Array.isArray(instagramData)) {
      return instagramData;
    }

    if (Array.isArray(instagramData?.data)) {
      return instagramData.data;
    }

    if (Array.isArray(instagramData?.data?.data)) {
      return instagramData.data.data;
    }
    return [];
  };

  const getPostInstaUserId = (mediaId) => {
    const post = getInstagramItems().find(
      (item) => String(item.id) === String(mediaId),
    );
    const postInstaUserId =
      post?.insta_user_id ||
      post?.instaUserId ||
      post?.account_id ||
      post?.accountId;

    if (postInstaUserId) return postInstaUserId;

    if (activeAccountId !== "all") return activeAccountId;

    const fallbackAccount = connectedAccounts[0];
    return fallbackAccount?.instaUserId || fallbackAccount?.id;
  };

  const getItemsFromPayload = (payload) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.data)) {
      return payload.data;
    }

    if (Array.isArray(payload?.data?.data)) {
      return payload.data.data;
    }

    return [];
  };

  const getMediaComments = (item) => {
    if (Array.isArray(item?.comments)) {
      return item.comments;
    }

    if (Array.isArray(item?.comments?.data)) {
      return item.comments.data;
    }

    return [];
  };

  const getCommentReplies = (comment) => {
    if (Array.isArray(comment?.replies)) {
      return comment.replies;
    }

    if (Array.isArray(comment?.replies?.data)) {
      return comment.replies.data;
    }

    return [];
  };

  const getTopLevelComments = (item) => {
    const comments = getMediaComments(item);

    const nestedReplyIds = new Set(
      comments.flatMap((comment) =>
        getCommentReplies(comment)
          .map((reply) => reply?.id)
          .filter(Boolean)
          .map(String),
      ),
    );

    return comments.filter(
      (comment) => !comment?.id || !nestedReplyIds.has(String(comment.id)),
    );
  };

  const getLikeCount = (item) =>
    item?.like_count ??
    item?.likes_count ??
    item?.likeCount ??
    item?.likes?.count ??
    0;

  const getCommentCount = (item) =>
    item?.comments_count ??
    item?.comment_count ??
    item?.commentCount ??
    item?.comments?.summary?.total_count ??
    getMediaComments(item).length ??
    0;

  // Saved / share counts come from getInstagramData (fields: saved, shares).
  const getSaveCount = (item) =>
    item?.saved ??
    item?.saved_count ??
    item?.saves ??
    item?.save_count ??
    0;

  const getShareCount = (item) =>
    item?.shares ??
    item?.share_count ??
    item?.shares_count ??
    0;


  // const getMediaTypeLabel = (item) => {
  //   const type = item?.media_type;
  //   if (!type) return null;
  //   if (type === "VIDEO") return "REEL";
  //   if (type === "CAROUSEL_ALBUM") return "CAROUSEL";
  //   if (type === "IMAGE") return "IMAGE";
  //   return type;
  // };

  const renderPostMedia = (item, className = styles.instagramMedia) => {
    if (item?.media_url && item?.media_type === "VIDEO") {
      return (
        <video
          src={item.media_url}
          className={className}
          controls
          playsInline
        />
      );
    }

    if (item?.media_url) {
      return (
        <img
          src={item.media_url}
          alt={item.caption || "Instagram post"}
          className={className}
          loading="lazy"
        />
      );
    }

    return <span className={styles.postFallback}>Instagram Post</span>;
  };

  const handleFetchComments = async (mediaId) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (selectedPostId === mediaId) {
      setSelectedPostId(null);
      resetCommentFilters();
      return;
    }

    setSelectedPostId(mediaId);
    resetCommentFilters();

    if (!userId) {
      setErrorMessage("Unable to find the logged-in  user ID.");
      return;
    }

    try {
      setCommentsLoadingId(mediaId);

      const result = await fetchInstagramMediaComments({
        userId,
        mediaId,
        instaUserId: getPostInstaUserId(mediaId),
      });
      console.log(result);

      const responsePayload = result?.data || result;
      const responseItems = getItemsFromPayload(responsePayload);
      const mediaDetails =
        responseItems.find((item) => String(item.id) === String(mediaId)) ||
        responsePayload;

      setCommentsByPostId((previousComments) => ({
        ...previousComments,
        [mediaId]: mediaDetails,
      }));
    } catch (error) {
      setErrorMessage(error.message || "Unable to fetch Instagram comments.");
    } finally {
      setCommentsLoadingId(null);
    }
  };
  //reply comments

  const handleToggleReplyBox = (commentId) => {
    setReplyBoxOpen((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const handleReplyChange = (commentId, value) => {
    setReplyInputs((prev) => ({
      ...prev,
      [commentId]: value,
    }));
  };

  const handleSendReply = async ({ commentId, mediaId, commentText }) => {
    setErrorMessage("");
    setSuccessMessage("");

    const message = (replyInputs[commentId] || "").trim();

    if (!message) {
      setErrorMessage("Reply message cannot be empty.");
      return;
    }

    try {
      setSendingReplyId(commentId);

      const result = await replyToComment({
        userId,
        commentId,
        message,
        mediaId,
        commentText,
        instaUserId: getPostInstaUserId(mediaId),
      });

      // Optimistically update the UI: append reply to the comment's replies
      setCommentsByPostId((prev) => {
        const mediaDetails = prev[mediaId] || {};
        const commentsList = getMediaComments(mediaDetails) || [];

        const updatedComments = commentsList.map((c) => {
          if (String(c.id) !== String(commentId)) return c;

          const existingReplies = getCommentReplies(c);

          // const newReply = (result?.data && result.data) ||
          //   result || { id: `reply-${Date.now()}`, text: message };
          const apiReply = result?.data?.data || result?.data || result;

          const newReply = {
            id: apiReply?.id || `reply-${Date.now()}`,
            text: apiReply?.text || apiReply?.message || message,
            timestamp:
              apiReply?.timestamp || new Date().toISOString(),
          };

          return {
            ...c,
            replies: {
              data: [...existingReplies, newReply],
            },
          };
        });

        // Build new mediaDetails with updated comments array
        const updatedMediaDetails = {
          ...mediaDetails,
          comments: {
            data: updatedComments,
          },
        };

        return {
          ...prev,
          [mediaId]: updatedMediaDetails,
        };
      });

      setReplyInputs((prev) => ({ ...prev, [commentId]: "" }));
      setReplyBoxOpen((prev) => ({ ...prev, [commentId]: false }));
      setSuccessMessage("Reply sent successfully.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to send reply.");
    } finally {
      setSendingReplyId(null);
    }
  };

  const instagramItems = getInstagramItems();


  const mediaTypeCounts = instagramItems.reduce(
    (counts, item) => {
      counts.all += 1;
      if (item.media_type === "VIDEO") counts.reel += 1;
      else if (item.media_type === "CAROUSEL_ALBUM") counts.carousel += 1;
      else counts.image += 1;
      return counts;
    },
    { all: 0, image: 0, carousel: 0, reel: 0 },
  );

  const filteredInstagramItems = instagramItems.filter((item) => {
    const matchesType =
      mediaTypeFilter === "all" ||
      (mediaTypeFilter === "IMAGE" &&
        item.media_type !== "VIDEO" &&
        item.media_type !== "CAROUSEL_ALBUM") ||
      (mediaTypeFilter === "CAROUSEL_ALBUM" &&
        item.media_type === "CAROUSEL_ALBUM") ||
      (mediaTypeFilter === "VIDEO" && item.media_type === "VIDEO");

    if (!matchesType) return false;

    return isWithinDateRange(
      item.timestamp,
      postDateRangeFilter,
      postCustomDateFrom,
      postCustomDateTo,
    );
  });

  const selectedMediaItem = selectedPostId
    ? instagramItems.find((item) => String(item.id) === String(selectedPostId))
    : null;
  const selectedMediaDetails = selectedPostId
    ? commentsByPostId[selectedPostId]
    : null;
  const selectedDisplayItem =
    selectedMediaItem && selectedMediaDetails && !Array.isArray(selectedMediaDetails)
      ? {
          ...selectedMediaItem,
          ...selectedMediaDetails,
        }
      : selectedMediaItem;
  const selectedCommentsList = getTopLevelComments(selectedDisplayItem);
  const selectedCommentsCount =
    getCommentCount(selectedDisplayItem) || selectedCommentsList.length;
  const selectedLikeCount = getLikeCount(selectedDisplayItem);
  const selectedShareCount = getShareCount(selectedDisplayItem);
  const selectedSaveCount = getSaveCount(selectedDisplayItem);

  // Sentiment breakdown across ALL loaded comments for this post (not the
  // filtered subset) — mirrors the "Comment Sentiment Breakdown" summary.
  const sentimentBreakdown = selectedCommentsList.reduce(
    (acc, comment) => {
      const sentiment = getCommentSentiment(comment);
      if (sentiment) acc[sentiment] += 1;
      return acc;
    },
    { positive: 0, neutral: 0, negative: 0 },
  );

  const normalizedSearch = commentSearch.trim().toLowerCase();

  const filteredCommentsList = selectedCommentsList
    .filter((comment) => {
      if (
        !isWithinDateRange(
          comment.timestamp,
          dateRangeFilter,
          customDateFrom,
          customDateTo,
        )
      ) {
        return false;
      }

      if (sentimentFilter !== "all") {
        if (getCommentSentiment(comment) !== sentimentFilter) return false;
      }

      if (normalizedSearch) {
        const haystack = `${comment.username || comment.from_name || ""} ${
          comment.text || comment.message || ""
        }`.toLowerCase();
        if (!haystack.includes(normalizedSearch)) return false;
      }

      return true;
    })
    .sort((a, b) => {
      const aTime = new Date(a.timestamp || 0).getTime();
      const bTime = new Date(b.timestamp || 0).getTime();
      return commentSortOrder === "oldest" ? aTime - bTime : bTime - aTime;
    });

  const handleCloseCommentsPopup = () => {
    setSelectedPostId(null);
    resetCommentFilters();
  };

  // Disconnects one specific connected account (never "all" — the
  // switcher's "All Accounts" pill has no disconnect action of its own).
  const handleDisconnectInstagram = async (instaUserId) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!userId) {
      setErrorMessage("Unable to find the logged-in user ID.");
      return;
    }

    if (!instaUserId || instaUserId === "all") {
      setErrorMessage("Select a specific account to disconnect.");
      return;
    }

    try {
      setIsDisconnecting(true);

      await disconnectInstagramAccount({
        userId,
        instaUserId,
      });

      const remainingAccounts = connectedAccounts.filter(
        (account) =>
          String(account.id) !== String(instaUserId) &&
          String(account.instaUserId) !== String(instaUserId),
      );
      saveConnectedAccounts(remainingAccounts);
      setSelectedPostId(null);
      setCommentsByPostId({});

      if (remainingAccounts.length === 0) {
        // No accounts left at all — fall back to the original
        // single-account "disconnected" state.
        setIsConnected(false);
        setInstagramData(null);
        setInstagramProfile(null);
        localStorage.removeItem("instagram_connected");
        sessionStorage.removeItem("instagram_connected");
        localStorage.removeItem("instagramProfile");
        localStorage.removeItem("instagramUsername");
        handleSelectAccount("all");
        setIsAccountSwitching(false);
      } else if (String(activeAccountId) === String(instaUserId)) {
        // The disconnected account was the active selection — fall back
        // to viewing everything that's still connected.
        handleSelectAccount("all");
      }

      window.history.replaceState({}, document.title, "/dashboard/instagram");

      setSuccessMessage("Instagram account disconnected successfully.");
    } catch (error) {
      setErrorMessage(error.message || "Unable to disconnect Instagram.");
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <DashboardLayout>
      <main className="content">
        <div className={styles.instagramPage}>
          {/* Fixed page bar — pinned under global header on scroll */}
          <div
            ref={stickyHeaderRef}
            className={styles.fixedPageHeader}
            style={{
              top: stickyHeaderOffset,
              left: fixedBarLeft,
              width: fixedBarWidth,
            }}
          >
            <div className={styles.fixedPageHeaderInner}>
              <div className={styles.pageHeaderLeft}>
                <div className={styles.pageHeaderIcon}>
                  <img src={instagramPng} alt="Instagram" />
                </div>
                <div className={styles.pageHeaderTitleRow}>
                  <h1>Instagram Integration</h1>
                  {!isConnected && (
                    <span
                      className={`${styles.statusBadge} ${styles.disconnectedStatus}`}
                    >
                      <span className={styles.statusDot}></span>
                      Not connected
                    </span>
                  )}
                </div>
              </div>

              {isConnected && (
                <div className={styles.pageHeaderRight}>
                  <div className={styles.accountPillScroller}>
                    {connectedAccounts.map((account) => (
                      <button
                        type="button"
                        key={account.id}
                        className={`${styles.accountPill} ${
                          activeAccountId === account.id
                            ? styles.accountPillActive
                            : ""
                        }`}
                        onClick={() => handleSelectAccount(account.id)}
                        title={
                          account.status && account.status !== "connected"
                            ? "Token expired — reconnect this account"
                            : `@${account.username}`
                        }
                      >
                        {account.avatarUrl ? (
                          <img
                            src={account.avatarUrl}
                            alt=""
                            className={styles.accountPillAvatar}
                          />
                        ) : (
                          <span className={styles.accountPillAvatarFallback}>
                            {account.username?.[0]?.toUpperCase() || "?"}
                          </span>
                        )}
                        @{account.username}
                        <span
                          className={`${styles.accountPillStatusDot} ${
                            account.status && account.status !== "connected"
                              ? styles.accountPillStatusDotWarn
                              : ""
                          }`}
                        />
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    className={styles.accountSwitcherConnectButton}
                    onClick={handleConnectInstagram}
                  >
                    <FiPlus />
                    Connect IG Account
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Reserves space so content starts below the fixed bar */}
          <div
            className={styles.fixedPageHeaderSpacer}
            style={{ height: fixedBarHeight }}
            aria-hidden="true"
          />

          {isConnected ? (
            <>
              <section className={styles.profileSummaryCard}>
                {isAccountSwitching ? (
                  <div
                    className={styles.profileSummarySkeleton}
                    aria-busy="true"
                    aria-label="Loading account details"
                  >
                    <div className={styles.profileSummaryMain}>
                      <span
                        className={`${styles.skeletonBlock} ${styles.skeletonAvatarLg}`}
                      />
                      <div className={styles.profileSummaryBody}>
                        <span
                          className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                          style={{ width: "40%", height: 16 }}
                        />
                        <span
                          className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                          style={{ width: "28%", height: 12, marginTop: 10 }}
                        />
                        <div
                          className={styles.profileSummaryStatsRow}
                          style={{ marginTop: 14 }}
                        >
                          <span
                            className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                            style={{ width: 90, height: 12 }}
                          />
                          <span
                            className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                            style={{ width: 70, height: 12 }}
                          />
                        </div>
                      </div>
                    </div>
                    <span
                      className={`${styles.skeletonBlock} ${styles.skeletonPill}`}
                    />
                  </div>
                ) : (
                <>
                <div className={styles.profileSummaryMain}>
                  <img
                    src={
                      activeAccountId === "all"
                        ? instagramProfile?.profile_picture_url || instagramPng
                        : activeAccount?.avatarUrl ||
                          instagramProfile?.profile_picture_url ||
                          instagramPng
                    }
                    alt={
                      activeAccountId === "all"
                        ? "Instagram"
                        : activeAccount?.username || "Instagram"
                    }
                    className={styles.profileSummaryAvatar}
                  />

                  <div className={styles.profileSummaryBody}>
                    <div className={styles.profileSummaryTitleRow}>
                      <h2 className={styles.profileSummaryName}>
                        {activeAccountId === "all"
                          ? `${connectedAccounts.length} account${
                              connectedAccounts.length === 1 ? "" : "s"
                            } connected`
                          : activeAccount?.username ||
                            instagramUsername ||
                            "—"}
                      </h2>

                      <span className={styles.connectedPill}>
                        <span className={styles.statusDot}></span>
                        {activeAccountId === "all"
                          ? "Combined view"
                          : "Active Connection"}
                      </span>
                    </div>

                    <p className={styles.profileSummaryHandle}>
                      {activeAccountId === "all" ? (
                        "Viewing combined data across every connected account"
                      ) : (
                        <>
                          @{activeAccount?.username || instagramUsername || "—"}
                          <span className={styles.profileSummaryDot}>·</span>
                        
                        </>
                      )}
                    </p>

                    <div className={styles.profileSummaryStatsRow}>
                      {instagramProfile?.followers_count !== undefined && (
                        <span className={styles.profileSummaryStat}>
                          <strong>
                            {Number(
                              instagramProfile.followers_count,
                            ).toLocaleString()}
                          </strong>
                          followers
                        </span>
                      )}

                      <span className={styles.profileSummaryStat}>
                        <strong>
                          {instagramProfile?.media_count !== undefined
                            ? Number(
                                instagramProfile.media_count,
                              ).toLocaleString()
                            : instagramItems.length}
                        </strong>
                        posts
                      </span>

                      {/* {instagramProfile?.total_reach !== undefined && (
                        <span className={styles.profileSummaryStat}>
                          <strong>
                            {Number(
                              instagramProfile.total_reach,
                            ).toLocaleString()}
                          </strong>
                          total reach
                        </span>
                      )} */}
                    </div>
                  </div>
                </div>

                <div className={styles.profileSummaryActions}>
                  <button
                    type="button"
                    className={styles.syncDataButton}
                    /* i want here on clicking sync data, fetchprofileinfo also call along with getinstragramdata */
                    
                    onClick={() => {
                      handleFetchInstagramData();
                      loadInstagramProfile();
                    }}
                    disabled={isFetching}
                    title="Sync Data"
                  >
                    <FiRefreshCw
                      className={isFetching ? styles.spinIcon : undefined}
                    />
                    {isFetching ? "Syncing..." : "Sync Data"}
                  </button>

                  {activeAccountId !== "all" && (
                    <button
                      type="button"
                      className={styles.disconnectButton}
                      onClick={() =>
                        handleDisconnectInstagram(activeAccountId)
                      }
                      disabled={isDisconnecting}
                    >
                      {isDisconnecting ? "Disconnecting..." : "Disconnect"}
                    </button>
                  )}
                </div>
                </>
                )}
              </section>
            </>
          ) : (
            <div className={styles.cardsGrid}>
              <section className={styles.connectCard}>
                <div className={styles.connectCardHeader}>
                  <div className={styles.instagramIcon}>
                    <img src={instagramPng} alt="Instagram" />
                  </div>

                  <h2>Connect Instagram</h2>
                </div>

                <p>
                  Sign in to Instagram and approve the required permissions.
                  Instagram will then redirect you back to Authkey.
                </p>

                <button
                  type="button"
                  className={styles.connectButton}
                  onClick={handleConnectInstagram}
                >
                  Connect with Instagram
                </button>

                <div className={styles.securityBox}>
                  <span className={styles.checkIcon}>✓</span>

                  <div>
                    <strong>Secure connection</strong>

                    <p>
                      Your Instagram password is not entered or stored inside
                      Authkey.
                    </p>
                  </div>
                </div>
              </section>

              <section className={styles.stepsCard}>
                <h2>How it works</h2>

                <div className={styles.stepItem}>
                  <span className={styles.stepNumber}>1</span>

                  <div>
                    <h3>Connect account</h3>
                    <p>Open the Instagram authorization page.</p>
                  </div>
                </div>

                <div className={styles.stepItem}>
                  <span className={styles.stepNumber}>2</span>

                  <div>
                    <h3>Approve access</h3>
                    <p>Sign in and grant the required permissions.</p>
                  </div>
                </div>

                <div className={styles.stepItem}>
                  <span className={styles.stepNumber}>3</span>

                  <div>
                    <h3>Fetch data</h3>
                    <p>Retrieve Instagram Posts using the Authkey API.</p>
                  </div>
                </div>
              </section>
            </div>
          )}

          <section className={styles.dataCard}>
            <div className={styles.dataHeader}>
              <div>
                {/* <span className={styles.sectionLabel}>Instagram Feed</span> */}

                <h2>Instagram Feed</h2>

                {/* <p>
                  View all posts from your connected Instagram account. Load
                  real-time comments or attach automation rules.
                </p> */}
              </div>

              {isConnected && (
                <div className={styles.dataActions}>
                  <Link
                    to="/dashboard/instagram-conversations"
                    className={styles.inboxIconButton}
                    aria-label="Open Instagram conversation inbox"
                  >
                    <FiMessageCircle className={styles.inboxIcon} />
                    <span className={styles.inboxLabel}>Messages</span>
                  </Link>
                </div>
              )}
            </div>

            {isConnected && (
              <div className={styles.mediaToolbar}>
                <div className={styles.mediaTypeTabs}>
                  <button
                    type="button"
                    className={`${styles.mediaTypeTab} ${
                      mediaTypeFilter === "all" ? styles.mediaTypeTabActive : ""
                    }`}
                    onClick={() => setMediaTypeFilter("all")}
                  >
                    <FiGrid className={styles.mediaTypeTabIcon} />
                    Post ({mediaTypeCounts.all})
                  </button>

                  <button
                    type="button"
                    className={`${styles.mediaTypeTab} ${
                      mediaTypeFilter === "VIDEO" ? styles.mediaTypeTabActive : ""
                    }`}
                    onClick={() => setMediaTypeFilter("VIDEO")}
                  >
                    <FiPlayCircle className={styles.mediaTypeTabIcon} />
                    Reel ({mediaTypeCounts.reel})
                  </button>
                </div>

                <div
                  className={styles.postDateFilterWrap}
                  ref={postDateFilterRef}
                >
                  <button
                    type="button"
                    className={`${styles.postDateFilterToggle} ${
                      postDateRangeFilter !== "all"
                        ? styles.postDateFilterToggleActive
                        : ""
                    }`}
                    onClick={() => setPostDateFilterOpen((open) => !open)}
                    aria-expanded={postDateFilterOpen}
                  >
                    <FiCalendar />
                    {DATE_RANGE_OPTIONS.find(
                      (option) => option.key === postDateRangeFilter,
                    )?.label || "All Time"}
                    <FiChevronDown
                      className={
                        postDateFilterOpen ? styles.filterChevronOpen : undefined
                      }
                    />
                  </button>

                  {postDateFilterOpen && (
                    <div className={styles.postDateFilterPanel}>
                      <div className={styles.dateRangePills}>
                        {DATE_RANGE_OPTIONS.map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            className={`${styles.dateRangePill} ${
                              postDateRangeFilter === option.key
                                ? styles.dateRangePillActive
                                : ""
                            }`}
                            onClick={() => {
                              setPostDateRangeFilter(option.key);
                              if (option.key !== "custom") {
                                setPostDateFilterOpen(false);
                              }
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>

                      {postDateRangeFilter === "custom" && (
                        <div className={styles.customDateRow}>
                          <input
                            type="date"
                            className={styles.customDateInput}
                            value={postCustomDateFrom}
                            onChange={(event) =>
                              setPostCustomDateFrom(event.target.value)
                            }
                          />
                          <span>to</span>
                          <input
                            type="date"
                            className={styles.customDateInput}
                            value={postCustomDateTo}
                            onChange={(event) =>
                              setPostCustomDateTo(event.target.value)
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {successMessage && (
              <div className={styles.successMessage}>{successMessage}</div>
            )}

            {errorMessage && (
              <div className={styles.errorMessage}>{errorMessage}</div>
            )}

            {isAccountSwitching || (isFetching && instagramItems.length === 0) ? (
              <div className={styles.mediaGrid} aria-busy="true" aria-label="Loading posts">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} className={styles.mediaCardSkeleton}>
                    <span
                      className={`${styles.skeletonBlock} ${styles.skeletonPostImage}`}
                    />
                    <div className={styles.postMeta}>
                      <span
                        className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                        style={{ width: "90%" }}
                      />
                      <span
                        className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                        style={{ width: "60%", marginTop: 8 }}
                      />
                    </div>
                    <div className={styles.postStatsGrid}>
                      {Array.from({ length: 4 }).map((__, statIndex) => (
                        <span
                          key={statIndex}
                          className={`${styles.skeletonBlock} ${styles.skeletonStat}`}
                        />
                      ))}
                    </div>
                    <div className={styles.postActions}>
                      <span
                        className={`${styles.skeletonBlock} ${styles.skeletonButton}`}
                      />
                      <span
                        className={`${styles.skeletonBlock} ${styles.skeletonButton}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : !instagramData ? (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>↻</div>

                <h3>No data fetched yet</h3>

                <p>
                  Connect Instagram and fetch the account data to display it
                  here.
                </p>
              </div>
            ) : instagramItems.length > 0 ? (
              filteredInstagramItems.length > 0 ? (
              <div className={styles.mediaGrid}>
                {filteredInstagramItems.map((item) => {
                  const mediaDetails = commentsByPostId[item.id];
                  const displayItem =
                    mediaDetails && !Array.isArray(mediaDetails)
                      ? {
                          ...item,
                          ...mediaDetails,
                        }
                      : item;
                  const postDate =
                    displayItem.timestamp &&
                    formatFullDateTime(displayItem.timestamp);

                  return (
                    <article key={item.id} className={styles.mediaCard}>
                      <div className={styles.postPreview}>
                        <div className={styles.mediaCardTop}>
                          <span
                            className={styles.postIdBadge}
                            title={`POST ID: ${item.id}`}
                          >
                            {formatShortPostId(item.id)}
                          </span>

                          {/* Populate active_rules_count on each media
                              item from the backend to surface this badge. */}
                          {displayItem.active_rules_count !== undefined && (
                            <span className={styles.rulesActiveBadge}>
                              <FiZap />
                              {displayItem.active_rules_count}{" "}
                              {displayItem.active_rules_count === 1
                                ? "Rule"
                                : "Rules"}{" "}
                              Active
                            </span>
                          )}
                        </div>

                        {renderPostMedia(displayItem)}
                      </div>

                      <div className={styles.postMeta}>
                        <p className={styles.postCaption}>
                          {displayItem.caption || "\u00A0"}
                        </p>
                      </div>

                      <div className={styles.postStatsGrid}>
                        <span
                          className={`${styles.postStat} ${styles.postStatLike}`}
                        >
                          <FiHeart />
                          {getLikeCount(displayItem)}
                        </span>

                        <span
                          className={`${styles.postStat} ${styles.postStatComment}`}
                        >
                          <FiMessageCircle />
                          {getCommentCount(displayItem)}
                        </span>

                        <span
                          className={`${styles.postStat} ${styles.postStatShare}`}
                        >
                          <FiShare2 />
                          {getShareCount(displayItem)}
                        </span>

                        <span
                          className={`${styles.postStat} ${styles.postStatSave}`}
                        >
                          <FiBookmark />
                          {getSaveCount(displayItem)}
                        </span>
                      </div>

                      <div className={styles.postActions}>
                        <button
                          type="button"
                          className={styles.loadCommentsButton}
                          onClick={() => handleFetchComments(item.id)}
                        >
                          <FiMessageCircle />
                          {selectedPostId === item.id
                            ? "Hide Comments"
                            : "Comments"}
                        </button>

                        <Link
                          to={{
                            pathname: INSTAGRAM_AUTOMATION_ROUTE,
                            state: {
                              mediaId: item.id,
                              mediaThumbnail:
                                item.media_type !== "VIDEO"
                                  ? item.media_url
                                  : null,
                              mediaCaption: item.caption || "",
                            },
                          }}
                          className={styles.automateReplyButton}
                        >
                          <FiZap />
                          Automate Reply
                        </Link>
                      </div>

                      <div className={styles.postMetaRow}>
                        {postDate && (
                          <span className={styles.postDate}>
                            <FiCalendar />
                            {postDate}
                          </span>
                        )}

                        {displayItem.permalink && (
                          <a
                            href={displayItem.permalink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.postPermalinkButton}
                            title="Open this post on Instagram"
                            aria-label="Open this post on Instagram"
                          >
                            <FiExternalLink />
                          </a>
                        )}
                      </div>

                      {/* <Link
                        to={{
                          pathname: INSTAGRAM_REPORT_ROUTE,
                          search: `?postId=${encodeURIComponent(item.id)}`,
                          state: {
                            mediaId: item.id,
                            mediaThumbnail:
                              item.media_type !== "VIDEO"
                                ? item.media_url
                                : null,
                            mediaCaption: item.caption || "",
                          },
                        }}
                        className={styles.viewOrmReportButton}
                      >
                        <FiBarChart2 />
                        View ORM Report for This Post
                      </Link> */}
                    </article>
                  );
                })}
              </div>
              ) : (
                <div className={styles.emptyState}>
                  <div className={styles.emptyIcon}>
                    <FiSearch />
                  </div>

                  <h3>No posts in this category</h3>

                  <p>Switch back to Post.</p>
                </div>
              )
            ) : (
              <div className={styles.dataResult}>
                <pre>{JSON.stringify(instagramData, null, 2)}</pre>
              </div>
            )}

            {selectedPostId && (
              <div
                className={styles.commentsOverlay}
                role="dialog"
                aria-modal="true"
                aria-labelledby="instagram-comments-title"
              >
                <div
                  className={styles.commentsBackdrop}
                  onClick={handleCloseCommentsPopup}
                />

                <div className={styles.commentsModal}>
                  <div className={styles.instagramViewer}>
                    <div className={styles.viewerPostPane}>
                      <button
                        type="button"
                        className={styles.commentsCloseButtonMobile}
                        onClick={handleCloseCommentsPopup}
                        aria-label="Close comments"
                      >
                        <FiX />
                      </button>

                      <div className={styles.viewerPostMedia}>
                        {renderPostMedia(
                          selectedDisplayItem,
                          styles.viewerMedia,
                        )}
                      </div>

                      <div className={styles.viewerPostMeta}>
                        <div className={styles.viewerMetaTopRow}>
                          <span className={styles.viewerFeedTag}>
                            Instagram Feed
                          </span>

                          {selectedDisplayItem?.timestamp && (
                            <span className={styles.viewerPostDate}>
                              {new Date(
                                selectedDisplayItem.timestamp,
                              ).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        {selectedDisplayItem?.caption && (
                          <p className={styles.viewerCaption}>
                            {selectedDisplayItem.caption}
                          </p>
                        )}

                        <div className={styles.viewerStatsBoxes}>
                          <div
                            className={`${styles.viewerStatBox} ${styles.viewerStatBoxLike}`}
                          >
                            <FiHeart />
                            <div>
                              <strong>{selectedLikeCount || 0}</strong>
                              <span>Total Likes</span>
                            </div>
                          </div>

                          <div
                            className={`${styles.viewerStatBox} ${styles.viewerStatBoxComment}`}
                          >
                            <FiMessageCircle />
                            <div>
                              <strong>{selectedCommentsCount || 0}</strong>
                              <span>Total Comments</span>
                            </div>
                          </div>

                          <div
                            className={`${styles.viewerStatBox} ${styles.viewerStatBoxShare}`}
                          >
                            <FiShare2 />
                            <div>
                              <strong>{selectedShareCount || 0}</strong>
                              <span>Total Shares</span>
                            </div>
                          </div>

                          <div
                            className={`${styles.viewerStatBox} ${styles.viewerStatBoxSave}`}
                          >
                            <FiBookmark />
                            <div>
                              <strong>{selectedSaveCount || 0}</strong>
                              <span>Total Saved</span>
                            </div>
                          </div>
                        </div>

                        <div className={styles.sentimentBreakdown}>
                          <span className={styles.sentimentBreakdownTitle}>
                            <FiSmile />
                            Comment Sentiment Breakdown
                          </span>

                          <div className={styles.sentimentBreakdownRow}>
                            <span
                              className={`${styles.sentimentPill} ${styles.sentimentPillPositive}`}
                            >
                              {sentimentBreakdown.positive} Positive
                            </span>

                            <span
                              className={`${styles.sentimentPill} ${styles.sentimentPillNeutral}`}
                            >
                              {sentimentBreakdown.neutral} Neutral
                            </span>

                            <span
                              className={`${styles.sentimentPill} ${styles.sentimentPillNegative}`}
                            >
                              {sentimentBreakdown.negative} Negative
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className={styles.viewerCommentsPane}>
                      <div className={styles.commentsModalHeader}>
                        <div>
                          <div className={styles.commentsModalTitleRow}>
                            <h3 id="instagram-comments-title">
                              Comments Stream
                            </h3>

                            <span className={styles.commentsShowingBadge}>
                              Showing {filteredCommentsList.length} of{" "}
                              {selectedCommentsList.length}
                            </span>
                          </div>

                          <p>
                            Filter loaded comments by date range, keywords,
                            or sentiment.
                          </p>
                        </div>

                        <div className={styles.commentsModalHeaderActions}>
                          <button
                            type="button"
                            className={`${styles.filterControlsButton} ${
                              filtersExpanded
                                ? styles.filterControlsButtonActive
                                : ""
                            }`}
                            onClick={() =>
                              setFiltersExpanded((prev) => !prev)
                            }
                          >
                            <FiSliders />
                            Filter Controls
                            <FiChevronDown
                              className={
                                filtersExpanded
                                  ? styles.filterChevronOpen
                                  : undefined
                              }
                            />
                          </button>

                          <button
                            type="button"
                            className={styles.commentsCloseButton}
                            onClick={handleCloseCommentsPopup}
                            aria-label="Close comments"
                          >
                            <FiX />
                          </button>
                        </div>
                      </div>

                      <div className={styles.commentsFilterBar}>
                        <div className={styles.dateRangeFilterRow}>
                          <span className={styles.dateRangeFilterLabel}>
                            <FiCalendar />
                            Date range:
                          </span>

                          <div className={styles.postDateFilterWrap}>
                            <button
                              type="button"
                              className={`${styles.postDateFilterToggle} ${
                                dateRangeFilter !== "all"
                                  ? styles.postDateFilterToggleActive
                                  : ""
                              }`}
                              onClick={() =>
                                setDateRangeMenuOpen((open) => !open)
                              }
                              aria-expanded={dateRangeMenuOpen}
                            >
                              {DATE_RANGE_OPTIONS.find(
                                (option) => option.key === dateRangeFilter,
                              )?.label || "All Time"}
                              <FiChevronDown
                                className={
                                  dateRangeMenuOpen
                                    ? styles.filterChevronOpen
                                    : undefined
                                }
                              />
                            </button>

                            {dateRangeMenuOpen && (
                              <div className={styles.postDateFilterPanel}>
                                <div className={styles.dateRangePills}>
                                  {DATE_RANGE_OPTIONS.map((option) => (
                                    <button
                                      key={option.key}
                                      type="button"
                                      className={`${styles.dateRangePill} ${
                                        dateRangeFilter === option.key
                                          ? styles.dateRangePillActive
                                          : ""
                                      }`}
                                      onClick={() => {
                                        setDateRangeFilter(option.key);
                                        setDateRangeMenuOpen(false);
                                      }}
                                    >
                                      {option.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {dateRangeFilter === "custom" && (
                          <div className={styles.customDateRow}>
                            <input
                              type="date"
                              className={styles.customDateInput}
                              value={customDateFrom}
                              onChange={(event) =>
                                setCustomDateFrom(event.target.value)
                              }
                            />
                            <span>to</span>
                            <input
                              type="date"
                              className={styles.customDateInput}
                              value={customDateTo}
                              onChange={(event) =>
                                setCustomDateTo(event.target.value)
                              }
                            />
                          </div>
                        )}

                        {filtersExpanded && (
                          <div className={styles.secondaryFilterRow}>
                            <div className={styles.commentSearchWrap}>
                              <FiSearch />
                              <input
                                type="text"
                                className={styles.commentSearchInput}
                                placeholder="Search comments or users..."
                                value={commentSearch}
                                onChange={(event) =>
                                  setCommentSearch(event.target.value)
                                }
                              />
                            </div>

                            <select
                              className={styles.commentFilterSelect}
                              value={sentimentFilter}
                              onChange={(event) =>
                                setSentimentFilter(event.target.value)
                              }
                            >
                              {SENTIMENT_OPTIONS.map((option) => (
                                <option key={option.key} value={option.key}>
                                  {option.label}
                                </option>
                              ))}
                            </select>

                            <select
                              className={styles.commentFilterSelect}
                              value={commentSortOrder}
                              onChange={(event) =>
                                setCommentSortOrder(event.target.value)
                              }
                            >
                              {SORT_OPTIONS.map((option) => (
                                <option key={option.key} value={option.key}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      <div className={styles.commentsModalBody}>
                        {commentsLoadingId === selectedPostId ? (
                          <div
                            className={styles.commentsList}
                            aria-busy="true"
                            aria-label="Loading comments"
                          >
                            {Array.from({ length: 4 }).map((_, index) => (
                              <div
                                key={index}
                                className={styles.commentItemSkeleton}
                              >
                                <span
                                  className={`${styles.skeletonBlock} ${styles.skeletonAvatarSm}`}
                                />
                                <div className={styles.commentMain}>
                                  <span
                                    className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                                    style={{ width: "35%", height: 12 }}
                                  />
                                  <span
                                    className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                                    style={{
                                      width: index % 2 === 0 ? "85%" : "65%",
                                      height: 12,
                                      marginTop: 10,
                                    }}
                                  />
                                  <span
                                    className={`${styles.skeletonBlock} ${styles.skeletonLine}`}
                                    style={{ width: "45%", height: 12, marginTop: 8 }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : filteredCommentsList.length > 0 ? (
                          <div className={styles.commentsList}>
                            {filteredCommentsList.map((comment, index) => (
                              <div
                                key={comment.id || index}
                                className={styles.commentItem}
                              >
                                <div className={styles.commentHeader}>
                                  <div className={styles.commentAvatar}>
                                    {(
                                      comment.username ||
                                      comment.from_name ||
                                      "IG"
                                    )
                                      .slice(0, 2)
                                      .toUpperCase()}
                                  </div>

                                  <div className={styles.commentMain}>
                                    <div className={styles.commentTopRow}>
                                      <div>
                                        <div className={styles.commentAuthorRow}>
                                          <h4 className={styles.commentAuthor}>
                                            {comment.username ||
                                              comment.from_name ||
                                              "Instagram user"}
                                          </h4>

                                          {getCommentSentiment(comment) && (
                                            <span
                                              className={`${styles.sentimentTag} ${
                                                SENTIMENT_TAG_CLASS[
                                                  getCommentSentiment(comment)
                                                ]
                                              }`}
                                            >
                                              {
                                                SENTIMENT_TAG_LABEL[
                                                  getCommentSentiment(comment)
                                                ]
                                              }
                                            </span>
                                          )}

                                          {getCommentTopic(comment) && (
                                            <span
                                              className={styles.commentTopicTag}
                                            >
                                              {getCommentTopic(comment)}
                                            </span>
                                          )}
                                        </div>

                                        <span className={styles.commentTime}>
                                          {formatFullDateTime(
                                            comment.timestamp,
                                          )}
                                        </span>
                                      </div>

                                      <button
                                        type="button"
                                        className={styles.replyButton}
                                        onClick={() =>
                                          handleToggleReplyBox(comment.id)
                                        }
                                      >
                                        {replyBoxOpen[comment.id]
                                          ? "Cancel"
                                          : "Reply"}
                                      </button>
                                    </div>

                                    <p className={styles.commentText}>
                                      {comment.text ||
                                        comment.message ||
                                        "No comment text available."}
                                    </p>

                                    <div className={styles.commentMeta}>
                                      {comment.like_count !== undefined && (
                                        <span>{comment.like_count} likes</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {replyBoxOpen[comment.id] && (
                                  <div className={styles.replyBox}>
                                    <div className={styles.replyComposerRow}>
                                      <textarea
                                        className={styles.replyInputPill}
                                        rows={1}
                                        placeholder="Write a reply to this comment..."
                                        maxLength={1000}
                                        value={replyInputs[comment.id] || ""}
                                        onChange={(event) =>
                                          handleReplyChange(
                                            comment.id,
                                            event.target.value,
                                          )
                                        }
                                        onKeyDown={(event) => {
                                          if (
                                            event.key === "Enter" &&
                                            !event.shiftKey
                                          ) {
                                            event.preventDefault();
                                            handleSendReply({
                                              commentId: comment.id,
                                              mediaId: selectedPostId,
                                              commentText: comment.text || comment.message,
                                            });
                                          }
                                        }}
                                      />

                                      <button
                                        type="button"
                                        className={styles.sendReplyIconButton}
                                        onClick={() =>
                                          handleSendReply({
                                            commentId: comment.id,
                                            mediaId: selectedPostId,
                                            commentText: comment.text || comment.message,
                                          })
                                        }
                                        disabled={
                                          sendingReplyId === comment.id ||
                                          !(
                                            replyInputs[comment.id] || ""
                                          ).trim()
                                        }
                                        aria-label="Send reply"
                                      >
                                        {sendingReplyId === comment.id ? (
                                          <span
                                            className={styles.sendSpinner}
                                          />
                                        ) : (
                                          <FiSend />
                                        )}
                                      </button>
                                    </div>

                                    <div className={styles.replyFooter}>
                                      <span
                                        className={
                                          styles.replyCharacterCount
                                        }
                                      >
                                        {(replyInputs[comment.id] || "").length}
                                        /1000
                                      </span>

                                      <button
                                        type="button"
                                        className={styles.cancelReplyLink}
                                        onClick={() =>
                                          handleToggleReplyBox(comment.id)
                                        }
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {getCommentReplies(comment).length > 0 && (
                                  <div className={styles.repliesList}>
                                    {getCommentReplies(comment).map(
                                      (reply, replyIndex) => (
                                        <div
                                          key={reply.id || replyIndex}
                                          className={styles.replyItem}
                                        >
                                          <div
                                            className={styles.replyItemHeader}
                                          >
                                            <span
                                              className={`${styles.replyIndicator} ${
                                                String(
                                                  reply.reply_type ||
                                                    reply.type ||
                                                    "",
                                                )
                                                  .toLowerCase()
                                                  .includes("auto")
                                                  ? styles.replyIndicatorAuto
                                                  : styles.replyIndicatorManual
                                              }`}
                                            >
                                              {String(
                                                reply.reply_type ||
                                                  reply.type ||
                                                  "",
                                              )
                                                .toLowerCase()
                                                .includes("auto")
                                                ? "Auto Reply"
                                                : "Reply"}
                                            </span>

                                            {reply.timestamp && (
                                              <span
                                                className={styles.replyTime}
                                              >
                                                {formatFullDateTime(
                                                  reply.timestamp,
                                                )}
                                              </span>
                                            )}
                                          </div>

                                          <p>
                                            {reply.text ||
                                              reply.message ||
                                              "No reply text available."}
                                          </p>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : selectedCommentsList.length > 0 ? (
                          <div className={styles.commentsFilterEmpty}>
                            <p className={styles.commentStatus}>
                              No comments match the current filters.
                            </p>
                            <button
                              type="button"
                              className={styles.reportResetButton}
                              onClick={resetCommentFilters}
                            >
                              Reset Filters
                            </button>
                          </div>
                        ) : (
                          <p className={styles.commentStatus}>
                            No comments found for this post.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </DashboardLayout>
  );
};

export default Instagram;